import  orderBy from 'lodash/orderBy'
import  differenceBy from 'lodash/differenceBy'
import { defineStore } from 'pinia'
import { contentTypes, type InsightPageModel } from '~/models'
import { removeHtmlTags } from '~/server/utils/search-indexer'
import type { SearchResultInsightItem } from '~/types/SearchResultItem'

export const useInsightModalStore = defineStore('insightModal', {
  state: () => ({
    availableCategories: [] as string[],
    items: [] as SearchResultInsightItem[],
    selectedInsightCategory: '',
    size: 4,
    initialItemsCount: 0,
    totalResults: 0,
    searchValue: '',
    authors: [''] as string[],
    selectedCapability: '',
    selectedAuthor: '',
    selectedSortValue: 'desc',
    layoutIsFullList: false,
    isGrid: true,
    openAsModal: false,
    unMutableLayoutIsFullList: false,
    readMore: false,
    previewItems: [],
    isLoading: true
  }),
  actions: {
    resetStates() {
      this.availableCategories = []
      this.items = []
      this.selectedInsightCategory = ''
      this.size = 4
      this.totalResults = 0
      this.searchValue = ''
      this.authors = ['']
      this.selectedCapability = ''
      this.selectedAuthor = ''
      this.selectedSortValue = 'desc'
      this.layoutIsFullList = false
      this.isGrid = true
      this.openAsModal = false
      this.unMutableLayoutIsFullList = false
      this.readMore = false
      this.previewItems = []
      this.isLoading = true
    },
    setSelectedInsightCategory(selectedInsightCategory: string) {
      usePaginationStore().changePage(1)
      this.selectedInsightCategory = selectedInsightCategory
    },
    setCloseViewAll() {
      if (!this.openAsModal) {
        return
      }
      this.clearFilters()
      if (!this.unMutableLayoutIsFullList) {
        this.setLayoutIsFullList(false)
      } else {
        this.size = this.totalResults
      }
      this.isGrid = true
      this.openAsModal = false
    },
    setSize(size: number) {
      this.size = size
    },
    setItems(items: SearchResultInsightItem[]) {

      if (items.length > 0) {
        const variance = differenceBy(items, this.items, 'id') // get all non-featured items
        this.items.push(...variance.slice(0, this.size - this.items.length)) // adds non-featured items into the items collection
      }
    },
    setFeaturedPreviewItems(items: InsightPageModel[]) {
      this.items = []
      
      // Adds featured items as default values of the collection
      const featuredItems: SearchResultInsightItem[] = []
      items = orderBy(items, i => i.elements.publishingDate.value, 'desc')
      items.map((item) => {
        featuredItems.push({
          id: item.system.id,
          title: item.elements.title.value,
          subTitle: removeHtmlTags(item.elements.subtitle.value),
          content: removeHtmlTags(item.elements.content.value),
          shortDescription: removeHtmlTags(item.elements.shortDescription.value),
          imgSrc: item.elements.cardImage.value[0]?.url,
          insightCategory: item.elements.insightCategory.value.map((c) => c.name),
          publishingDate: item.elements.publishingDate.value?.toString() || '',
          urlSlug: item.elements.urlSlug.value,
          richTextCustomElement: item.elements.richTextInterpreterRichText.value,
          isFeatured: true
        })
      })

      featuredItems.forEach(f => {
        if (f.insightCategory[0] === this.selectedInsightCategory || this.selectedInsightCategory == '') {
          this.items.push(f)
        }
      })
    },
    setAuthors(authors: string[]) {
      this.authors = authors
    },
    setAvailableCategories(availableCategories: string[]) {
      this.availableCategories = availableCategories
    },

    setSelectedCapability(selectedCapability: string) {
      usePaginationStore().changePage(1)
      this.selectedCapability = selectedCapability
    },
    setSelectedAuthor(selectedAuthor: string) {
      usePaginationStore().changePage(1)
      this.selectedAuthor = selectedAuthor
    },
    setTotalResults(totalResults: number) {
      this.totalResults = totalResults
    },
    setInitialItemsCount(initialItemsCount: number) {
      this.initialItemsCount = initialItemsCount
    },
    setLayoutIsFullList(isFullList: boolean) {
      if (isFullList) {
        this.size = 20
      } else {
        this.size = 4
      }
      this.layoutIsFullList = isFullList
    },
    setViewAllPreview() {
      this.openAsModal = true
      this.setLayoutIsFullList(true)
    },
    setIsGrid(isGrid: boolean) {
      this.isGrid = isGrid
    },
    setSortValue(sortValue: string) {
      this.selectedSortValue = sortValue
    },
    clearFilters() {
      const pageStore = usePageStore()
      const pageType = pageStore.page?.system.type
      if (pageType != contentTypes.capabilities_page.codename) {
        this.selectedCapability = ''
      }
      this.selectedInsightCategory = ''
      if (pageType != contentTypes.people_page.codename) {
        this.selectedAuthor = ''
      }
      this.selectedSortValue = 'desc'
      this.searchValue = ''
      usePaginationStore().changePage(1)
    },
    setSearchValue(value: string) {
      this.searchValue = value
    },
    transformDate(isoDate: string | null): string {
      if (!isoDate) return ''
      const date = new Date(isoDate)
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear().toString().slice(-2) // Take last two digits of year

      return `${month}.${day}.${year}`
    },
    shortenedParagraph(content: string, shortDescription: string) {
      if (shortDescription) {
        return shortDescription.length > 100
          ? shortDescription.substring(0, 100) + '...'
          : shortDescription
      }
      return content.length > 100 ? content.substring(0, 100) + '...' : content
    },
    setReadMore(read: boolean) {
      this.readMore = read
    },
    addItems(items: SearchResultInsightItem[]) {
      this.items = this.items.concat(items)
    },
    setLoading(isLoading: boolean) {
      this.isLoading = isLoading
    }
  }
})
